/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import { omit, isEmpty } from 'lodash';
import numeral from 'numeral';
import moment from 'moment';

import { getAccountDescriptor } from 'services/Accounts';
import {
  TRADITIONAL_IRA,
  ROTH_IRA,
  ROLLOVER_IRA,
  ROTH_ROLLOVER_IRA,
  EDUCATION_SAVINGS_ACCOUNTS,
  TRANSFER_ON_DEATH
} from 'services/AccountTypes';
import { formatSSN, formatTaxNumber } from 'services/Formats';
import { getAge } from 'services/Calculation';
import { isValidBeneficiaryDob } from 'services/Dates';
import { isProductEligibleForBeneficiary } from 'services/Products';
import { beneficiariesEnabled } from 'services/FeatureFlags';

export const newPrimaryBeneficiaryInitialState = {
  birthDate: '',
  businessEntityName: '',
  businessEntityNames: null,
  designationId: 0,
  distributionAmount: 0.0,
  firstName: '',
  lastName: '',
  middleName: '',
  nameSuffix: '- -',
  relationshipId: 0,
  taxId: '',
  typeId: 1
};

export const newPrimaryBeneficiaryInitialStateV2 = {
  birthDate: '',
  businessEntityName: '',
  businessEntityNames: null,
  name: '',
  designationId: 0,
  distributionAmount: '00.00',
  distributionAmountFormated: '00.00',
  firstName: '',
  lastName: '',
  middleName: '',
  nameSuffix: '',
  relationshipId: 0,
  taxId: '',
  deleted:false,
  typeId: 1
};

export const newSecondaryBeneficiaryInitialState = {
  birthDate: '',
  businessEntityName: '',
  businessEntityNames: null,
  designationId: 1,
  distributionAmount: 0.0,
  firstName: '',
  lastName: '',
  middleName: '',
  nameSuffix: '- -',
  relationshipId: 0,
  taxId: '',
  typeId: 1
};

export const beneficiaryTypeOptions = [
  {
    label: 'Individual',
    value: 1
  },
  {
    label: 'Entity',
    value: 2
  }
];

export const beneficiaryDesignationOptions = [
  {
    label: 'Primary',
    value: 0
  },
  {
    label: 'Secondary',
    value: 1
  }
];

const SUFFIX_DEFAULT_VALUE = '- -';

export const beneficiarySuffixOptions = [
  {
    label: SUFFIX_DEFAULT_VALUE,
    value: SUFFIX_DEFAULT_VALUE
  },
  {
    label: 'Jr.',
    value: 'Jr.'
  },
  {
    label: 'Sr.',
    value: 'Sr.'
  },
  {
    label: 'I',
    value: 'I'
  },
  {
    label: 'II',
    value: 'II'
  },
  {
    label: 'III',
    value: 'III'
  },
  {
    label: 'IV',
    value: 'IV'
  },
  {
    label: 'V',
    value: 'V'
  }
];

export const beneficiarySuffixOptionsV2 = [
  {
    label: 'Select',
    value: 0
  },
  {
    label: 'Jr.',
    value: 'Jr.'
  },
  {
    label: 'Sr.',
    value: 'Sr.'
  },
  {
    label: 'I',
    value: 'I'
  },
  {
    label: 'II',
    value: 'II'
  },
  {
    label: 'III',
    value: 'III'
  },
  {
    label: 'IV',
    value: 'IV'
  },
  {
    label: 'V',
    value: 'V'
  }
];

export const beneficiaryRelationshipOptions = [
  {
    label: 'Select',
    value: 0
  },
  {
    label: 'Parent',
    value: 100
  },
  {
    label: 'Spouse',
    value: 101
  },
  {
    label: 'Child',
    value: 102
  },
  {
    label: 'Sibling',
    value: 103
  },
  {
    label: 'Other',
    value: 104
  }
];

export const successorBeneficiaryRelationshipOptions = [
  {
    label: 'Select',
    value: 0
  },
  {
    label: 'Parent',
    value: 100
  },
  {
    label: 'Spouse',
    value: 101
  },
  {
    label: 'Child',
    value: 102
  },
  {
    label: 'Sibling',
    value: 103
  }
];

export const BUSINESS_RELATIONSHIP_ID = 105;

export const NOT_PENDING_STATUS = 1;

const YYYY_MM_DD_REGEX = /^\d{4}[-/]\d{2}[-/]\d{2}$/;

const MM_DD_YYYY_REGEX = /^\d{2}[-/]\d{2}[-/]\d{4}$/;

const DATE_SEPARATOR_REGEX = /[-/]/;

export const getPendingStatusText = (pendingUpdateId) => {
  if (pendingUpdateId === 2) {
    return 'You have a pending change that requires additional processing. Please check back in 10-15 minutes.';
  } else if (pendingUpdateId === 3) {
    return 'You have a pending change that requires additional processing. Please check back in 2-3 business days.';
  }
  return null;
};

export const isRegistrationPending = (beneficiaries) => {
  const beneficiariesPending = beneficiaries.find(
    (bene) => bene.pendingUpdateId === 2 || bene.pendingUpdateId === 3
  );
  if (beneficiariesPending && beneficiariesPending.pendingUpdateId) {
    return {
      pendingText: getPendingStatusText(beneficiariesPending.pendingUpdateId),
      isPending: true
    };
  }

  return null;
};

export const validAccountTypesForBeneficiaries = [
  TRADITIONAL_IRA,
  ROTH_IRA,
  ROLLOVER_IRA,
  ROTH_ROLLOVER_IRA,
  EDUCATION_SAVINGS_ACCOUNTS,
  TRANSFER_ON_DEATH
];

export const getBeneficiaryRelationshipName = (beneficiary) => {
  if (beneficiary.relationshipId === BUSINESS_RELATIONSHIP_ID) {
    return 'Entity';
  }
  const option = beneficiaryRelationshipOptions.find(
    (option) => option.value === beneficiary.relationshipId
  );
  return option ? option.label : '-';
};

export const isBeneficiaryASpouse = (beneficiary) =>
  parseInt(beneficiary.relationshipId) === 101;

export const getBeneficiaryTypeName = (beneficiary) => {
  const option = beneficiaryTypeOptions.find(
    (option) => option.value === beneficiary.typeId
  );
  return option ? option.label : '-';
};
export const isBeneficiaryACustomer = (beneficiary) =>
  parseInt(beneficiary.typeId) === 1;
export const isBeneficiaryABusinessEntity = (beneficiary) =>
  parseInt(beneficiary.typeId) === 2;
export const isBeneficiaryPrimary = (beneficiary) =>
  beneficiary.designationId === 0;
export const getBeneficiaryDesignationName = (beneficiary) => {
  const option = beneficiaryDesignationOptions.find(
    (option) => option.value === beneficiary.designationId
  );
  return option ? option.label : '-';
};

export const getHasAnyDobError = (beneficiaries, isEditing) => {
  if (isEditing) {
    return beneficiaries.some((bene) => {
      return !isEmpty(bene.birthDate) && !isValidBeneficiaryDob(bene.birthDate);
    });
  }
  return false;
};

export const getBeneficiaryDisplayName = (beneficiary) => {
  return isBeneficiaryACustomer(beneficiary)
    ? [beneficiary.firstName, beneficiary.middleName, beneficiary.lastName, beneficiary.nameSuffix].filter(Boolean).join(' ')
    : beneficiary.businessEntityNames.map((v) => v).join(' ');
};

export const getBeneficiaryDisplayTaxId = (beneficiary) => {
  if (!beneficiary.taxId) {
    return '-';
  }
  return isBeneficiaryACustomer(beneficiary)
    ? formatSSN(beneficiary.taxId)
    : formatTaxNumber(beneficiary.taxId);
};

export const getBeneficiaryDisplayBirthdate = (beneficiary) => {
  if (!beneficiary.birthDate) {
    return '-';
  }
  const { birthDate } = beneficiary;
  const separator = '/';
  const format = `MM${separator}DD${separator}YYYY`;
  return moment.utc(birthDate).format(format);
};

export const getDisplayBirthdate = (birthDate) => {
  if (!birthDate) {
    return '-';
  }
  const separator = '/';
  const format = `MM${separator}DD${separator}YYYY`;
  return moment.utc(birthDate).format(format);
};

// convert from YYYY-MM-DD to MM-DD-YYYY format
export const beaconToAamFormatter = (date) => {
  if (date && YYYY_MM_DD_REGEX.test(date)) {
    const [year, month, day] = date.split(DATE_SEPARATOR_REGEX);
    return `${month}/${day}/${year}`;
  }
  return date || '';
}

// convert from MM-DD-YYYY to YYYY-MM-DD
export const aamToBeaconFormatter = (date) => {
  if (date && MM_DD_YYYY_REGEX.test(date)) {
    const [month, day, year] = date.split(DATE_SEPARATOR_REGEX);
    return `${year}-${month}-${day}`;
  }
  return date || '';
}

export const cleanBeneficiaryWTypeId = (beneficiary, typeId) => {
  return {
    ...beneficiary,
    birthDate: '',
    businessEntityName: '',
    businessEntityNames: null,
    distributionAmount: 0.0,
    firstName: '',
    lastName: '',
    middleName: '',
    nameSuffix: '- -',
    taxId: '',
    name: '',
    relationshipId: parseInt(typeId) === 2 ? BUSINESS_RELATIONSHIP_ID : 0,
    relationshipName: parseInt(typeId) === 2 ? 'Entity' : ''
  };
};

const cleanTaxId = (taxId) => {
  return taxId ? taxId.replace(/-/g, '') : taxId;
};

export const generateBeneficiaryPayload = (
  accounts,
  previousBeneficiaries,
  beneficiaries
) => {
  const benePayload = [];
  if (isEmpty(beneficiaries)) {
    accounts.forEach((account) => {
      previousBeneficiaries.forEach((bene) => {
        let beneWithAccountDescriptor = {
          ...bene,
          accountDescriptor: getAccountDescriptor(account)
        };
        const cleanedBeneficiary = omit(beneWithAccountDescriptor, [
          '_id',
          'businessEntityName',
          'deleted'
        ]);

        benePayload.push(cleanedBeneficiary);
      });
    });
    return benePayload;
  }

  // filter accounts to add beneficiary for eligible product types
  accounts.filter(isProductEligibleForBeneficiary).forEach((account) => {
    beneficiaries.forEach((bene) => {
      let beneWithAccountDescriptor = {
        ...bene,
        accountDescriptor: getAccountDescriptor(account)
      };
      if (isBeneficiaryABusinessEntity(bene)) {
        //Need to put businessEntityNames in an array
        beneWithAccountDescriptor['businessEntityNames'] = [
          bene.businessEntityName
        ];
      }
      const cleanedBeneficiary = omit(beneWithAccountDescriptor, [
        '_id',
        'businessEntityName',
        'deleted',
        'distributionAmountFormated',
        'name',
        'relationshipName',
      ]);

      //ensure typeId value is an integer
      cleanedBeneficiary['typeId'] = parseInt(cleanedBeneficiary['typeId']);
      if(beneficiariesEnabled()) {
        //if in new benes page, ensure taxId is valid
        cleanedBeneficiary['taxId'] = cleanTaxId(cleanedBeneficiary['taxId']);
      }
      if (
        cleanedBeneficiary.nameSuffix &&
        cleanedBeneficiary.nameSuffix === SUFFIX_DEFAULT_VALUE
      ) {
        //remove default value on suffix and set to empty string
        cleanedBeneficiary['nameSuffix'] = '';
      }
      benePayload.push(cleanedBeneficiary);
    });
  });

  return benePayload;
};

export const generateBenficiaryUpdatePayload = (accounts, beneficiaries) => {
  const benePayload = [];
  const filterDeleted = beneficiaries.filter((bene) => !bene.deleted);
  filterDeleted.forEach((bene) => {
    let beneWithAccountDescriptor = {
      ...bene,
      accountDescriptor: getAccountDescriptor(accounts[0])
    };
    if (isBeneficiaryABusinessEntity(bene)) {
      //Need to put businessEntityNames in an array
      beneWithAccountDescriptor['businessEntityNames'] = [
        bene.businessEntityName
      ];
    }
    const cleanedBeneficiary = omit(beneWithAccountDescriptor, [
      '_id',
      'businessEntityName',
      'deleted',
      'distributionAmountFormated',
      'name',
      'relationshipName',
    ]);

    //ensure typeId value is an integer
    cleanedBeneficiary['typeId'] = parseInt(cleanedBeneficiary['typeId']);
    cleanedBeneficiary['taxId'] = cleanedBeneficiary['taxId']
      ? cleanedBeneficiary['taxId'].replaceAll('-', '')
      : null;
    if (
      cleanedBeneficiary.nameSuffix &&
      cleanedBeneficiary.nameSuffix === SUFFIX_DEFAULT_VALUE
    ) {
      //remove default value on suffix and set to empty string
      cleanedBeneficiary['nameSuffix'] = '';
    }
    benePayload.push(cleanedBeneficiary);
  });

  return benePayload;
};

export const isDeleteAllBeneficiaries = (beneficiaries) => {
  return beneficiaries.every((bene) => bene.deleted);
};

export const generateBeneficiaryMap = (beneficiaries) => {
  const map = [];
  beneficiaries.forEach((beneficiary) => {
    const { accountDescriptor } = beneficiary;
    if (accountDescriptor && accountDescriptor.accountId) {
      const mappedBenes = map[accountDescriptor.accountId] || [];
      mappedBenes.push(beneficiary);
      map[accountDescriptor.accountId] = mappedBenes;
    }
  });
  return map;
};

export const isBeneficiariesValid = (beneficiaries) => {
  return beneficiaries.every((bene) => {
    if (isBeneficiaryACustomer(bene)) {
      return (
        bene.firstName.length &&
        bene.lastName.length &&
        bene.relationshipId !== 0
      );
    }
    if (isBeneficiaryABusinessEntity(bene)) {
      return bene.businessEntityName.length;
    }
  });
};

export const checkIsTodError = (beneficiaries, isTOD) => {
  if (isTOD) {
    if (isEmpty(beneficiaries)) {
      return true;
    }
    if (!isEmpty(beneficiaries)) {
      const filteredBeneficiaries = beneficiaries.filter(
        (bene) => !bene.deleted
      );
      return filteredBeneficiaries.length === 0;
    }
  }
  return false;
};

export const isPrimaryBeneficiaryError = (
  primaryBeneficiaries,
  secondaryBeneficiaries
) => {
  if (
    isEmpty(primaryBeneficiaries) ||
    isDeleteAllBeneficiaries(primaryBeneficiaries)
  ) {
    if (isDeleteAllBeneficiaries(secondaryBeneficiaries)) {
      return false;
    }
    if (!isEmpty(secondaryBeneficiaries)) {
      return true;
    }
  }
};

export const primaryBeneficiaryErrorText =
  'A Primary Beneficiary is required when you have a Secondary Beneficiary on file.';

export const generateDistributionError = (
  beneficiaries,
  isPrimary = false,
  isEducationSavings = false
) => {
  let distribution = numeral(0);
  let errorMessage = '';
  if (!isEmpty(beneficiaries)) {
    const filteredBeneficiaries = beneficiaries.filter((bene) => !bene.deleted);
    if (isPrimary) {
      if (filteredBeneficiaries.length > 1) {
        if (isEducationSavings) {
          errorMessage = 'Successor Beneficiaries should total 100%';
        } else {
          errorMessage = 'Primary Beneficiaries should total 100%';
        }
      } else {
        errorMessage = 'Primary Beneficiary should total 100%';
      }
    } else {
      filteredBeneficiaries.length > 1
        ? (errorMessage = 'Secondary Beneficiaries should total 100%')
        : (errorMessage = 'Secondary Beneficiary should total 100%');
    }
    if (!isEmpty(filteredBeneficiaries)) {
      if (
        filteredBeneficiaries.some(
          (bene) => bene.distributionAmount === 0 || !bene.distributionAmount
        )
      ) {
        errorMessage = 'Distribution % is required to be greater than 0';
        return {
          isPrimary: isPrimary,
          isValid: false,
          errorMessage: errorMessage
        };
      }
      filteredBeneficiaries.forEach((bene) => {
        distribution.add((bene.distributionAmount * 100) / 100);
      });
      if (
        distribution.value() < 1 ||
        distribution.value() > 1 ||
        !distribution.value()
      ) {
        return {
          isPrimary: isPrimary,
          isValid: false,
          errorMessage: errorMessage
        };
      }
    }
  }

  return {
    isValid: true,
    errorMessage: ''
  };
};

export const educationSavingsWarning = (
  beneficiaries,
  isEditingBeneficiaries,
  isEducationSavings
) => {
  if (isEducationSavings) {
    const beneficiaryOverAgeLimit = beneficiaries.some(
      (bene) => getAge(bene.birthDate) >= 25
    );
    if (beneficiaryOverAgeLimit && isEditingBeneficiaries) {
      return true;
    }
  }

  return false;
};

export const isSpouseError = (beneficiaries, isEditingBeneficiaries) => {
  if (isEditingBeneficiaries) {
    let count = 0;
    beneficiaries.forEach((bene) => {
      if (isBeneficiaryASpouse(bene)) {
        count++;
      }
    });
    return count > 1;
  }
};

export const BENEFICIARY_EVENT_TYPES = {
  ADD: 'ADD_BENEFICIARY',
  UPDATE: 'UPDATE_BENEFICIARY',
  DELETE: 'DELETE_BENEFICIARY'
};

export const buildBusinessEvent = (eventType, email, customer, accounts) => {
  const formattedAccounts = accounts.map((acct) => {
    const accountDescriptor = getAccountDescriptor(acct);
    return {
      sourceAccount: {
        agreementID: accountDescriptor.accountId,
        agreementNumber: accountDescriptor.accountNumber,
        productType: accountDescriptor.productType,
        checkSum: parseInt(accountDescriptor.accountChecksum)
      }
    };
  });

  return {
    eventType: eventType,
    emailAddress: email,
    brandingProfile: 'RETAIL',
    entityKey: {
      businessPartyId: customer.businessPartyId,
      uberId: customer.uberId
    },
    beneficiaries: [...formattedAccounts]
  };
};
